import { ref } from 'vue';
import customerService from '@/services/customerService';
import type { Customer } from '@/types/customer';

export const useCustomerSearch = () => {
  const searchQuery = ref('');
  const searchResults = ref<Customer[]>([]);
  const isSearching = ref(false);

  let searchTimeout: NodeJS.Timeout;

  const handleSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(async () => {
      if (!searchQuery.value.trim()) {
        searchResults.value = [];
        return;
      }

      // Validate minimum 3 characters for search
      if (searchQuery.value.trim().length < 3) {
        searchResults.value = [];
        return;
      }

      isSearching.value = true;
      try {
        const response = await customerService.searchCustomers(searchQuery.value, 20);
        
        // Handle flattened response from axios interceptor
        if (response && response.customers && Array.isArray(response.customers)) {
          searchResults.value = response.customers;
        } else if (Array.isArray(response)) {
          // Axios flattened the response, so this is actually the customers array
          searchResults.value = response;
        } else if (response && typeof response === 'object') {
          // Check if response itself is the customers array (flattened)
          const firstItem = response[0];
          if (firstItem && typeof firstItem === 'object' && 'id' in firstItem && 'name' in firstItem) {
            searchResults.value = Object.values(response);
          } else {
            console.error('Unexpected response structure:', response);
            searchResults.value = [];
          }
        } else {
          console.error('Unexpected response structure:', response);
          searchResults.value = [];
        }
      } catch (error) {
        console.error('Error searching customers:', error);
        searchResults.value = [];
      } finally {
        isSearching.value = false;
      }
    }, 500);
  };

  const clearSearch = () => {
    searchQuery.value = '';
    searchResults.value = [];
    clearTimeout(searchTimeout);
  };

  return {
    searchQuery,
    searchResults,
    isSearching,
    handleSearch,
    clearSearch,
  };
};
